/**
 * Customer (invitee) portal API.
 *
 * ⚠️ THIS USES ITS OWN TOKEN, under its own localStorage key. The host token
 * lives under `token`; mixing them would either send a host token to a customer
 * route or leave a customer signed in as a host on a shared browser. The server
 * rejects the cross case anyway (`aud` is verified strictly), but the two must
 * not share storage either.
 */
import axios from 'axios';
import i18n from '../i18n';
import {
  CustomerMe,
  EntitlementResolution,
  PointsHistoryEntry,
  PurchasableResponse,
  ShopResponse,
} from '../types/customer';

const API_URL = `${process.env.REACT_APP_API_URL}/api`;

/** Distinct from the host's `token` key on purpose. */
export const CUSTOMER_TOKEN_KEY = 'saillab_customer_token';

export const getCustomerToken = (): string | null => {
  try {
    return localStorage.getItem(CUSTOMER_TOKEN_KEY);
  } catch {
    // Private mode / blocked storage — treat as signed out rather than throwing
    // and taking the whole booking page down with it.
    return null;
  }
};

export const setCustomerToken = (token: string): void => {
  try {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  } catch {
    /* ignore — the session just won't survive a reload */
  }
};

export const clearCustomerToken = (): void => {
  try {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Receipt for something bought WITHOUT an account, so the booking that prompted
 * the purchase can actually be completed.
 *
 * ⚠️ NOT A SESSION, and stored nowhere near one. It goes in sessionStorage, not
 * localStorage: it is scoped to the tab that did the buying and dies with it,
 * because it exists only to finish the booking already in progress. It is never
 * sent as an Authorization header — the server takes it in the booking body and
 * treats it as a pointer to a wallet, nothing more.
 */
const PURCHASE_GRANT_KEY = 'saillab_purchase_grant';

export const getPurchaseGrant = (): string | null => {
  try {
    return sessionStorage.getItem(PURCHASE_GRANT_KEY);
  } catch {
    return null;
  }
};

export const setPurchaseGrant = (token: string): void => {
  try {
    sessionStorage.setItem(PURCHASE_GRANT_KEY, token);
  } catch {
    /* ignore — they can still sign in and book */
  }
};

export const clearPurchaseGrant = (): void => {
  try {
    sessionStorage.removeItem(PURCHASE_GRANT_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Adopt the session the server hands back at the end of a signed-out checkout.
 *
 * The server issues one ONLY when this very purchase created the account, so
 * accepting it here cannot sign anyone into an identity that existed before —
 * see `issueSessionForCheckoutClaim`. A real session supersedes a receipt, so
 * any purchase grant left in the tab is dropped: the two are alternative
 * answers to "who is spending this", and keeping both invites the stale one to
 * be used.
 *
 * Returns whether we are now signed in, so the checkout can put the
 * set-a-password step in front of the buyer.
 */
const adoptCheckoutSession = (sessionToken?: string | null): boolean => {
  if (!sessionToken) return false;
  setCustomerToken(sessionToken);
  clearPurchaseGrant();
  return true;
};

const authConfig = () => {
  const token = getCustomerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// ========== AUTH ==========

export const login = async (email: string, password: string): Promise<string> => {
  const response = await axios.post(`${API_URL}/customer/auth/login`, { email, password });
  const token = response.data?.token;
  if (!token) throw new Error('invalid_credentials');
  setCustomerToken(token);
  // A real session supersedes a receipt. Leaving one behind would keep a
  // pointer to whichever wallet was bought into, next to an account that may
  // not be the same person.
  clearPurchaseGrant();
  return token;
};

/**
 * Start the reset / first-password flow.
 *
 * Always resolves, even for an unknown address — the server answers identically
 * either way, and the UI must not reveal the difference.
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  // Send the page's language so the email matches what they are reading. The
  // customer has no stored preference — they may not have an account at all.
  await axios.post(`${API_URL}/customer/auth/reset-request`, {
    email,
    language: i18n.language?.startsWith('ja') ? 'ja' : 'en',
  });
};

export const completePasswordReset = async (
  token: string,
  password: string
): Promise<string> => {
  const response = await axios.post(`${API_URL}/customer/auth/reset`, { token, password });
  const jwt = response.data?.token;
  if (!jwt) throw new Error('reset_failed');
  setCustomerToken(jwt);
  clearPurchaseGrant();
  return jwt;
};

export const setPassword = async (password: string): Promise<void> => {
  await axios.post(`${API_URL}/customer/auth/set-password`, { password }, authConfig());
};

export const logout = (): void => clearCustomerToken();

// ========== BUYING POINTS ==========

export interface PointPurchaseInit {
  clientSecret: string;
  transactionId: string;
  amount: number;
  currency: string;
  pointsGranted: number;
  /** Both required by stripe.js: the intent lives on the CONNECTED account. */
  publishableKey: string;
  connectedAccountId: string;
  /**
   * Present only when a signed-out buyer named an address that has no account
   * at all. Handed straight back at confirm, where the server exchanges it for
   * a real session on the identity this purchase created.
   *
   * ⚠️ HELD IN COMPONENT STATE, NEVER IN STORAGE. It belongs to one checkout
   * and dies with it; persisting it would leave a claim on a brand-new account
   * lying around in the browser long after the purchase it came from.
   */
  checkoutClaimToken?: string;
}

/**
 * Which shops a host has anything in. Used to build the product navigation, so
 * a link is only ever offered to a page with something on it.
 */
export const getShopSummary = async (
  username: string
): Promise<{ plans: number; points: number }> => {
  const response = await axios.get(
    `${API_URL}/customer/shops/${encodeURIComponent(username)}`
  );
  return { plans: response.data?.plans ?? 0, points: response.data?.points ?? 0 };
};

/**
 * One host's shelf of plans or point packs, for the standalone shop pages.
 *
 * Public — no auth header needed. `kind` is the URL word, not the database
 * kind, because these are addressed as /:username/shop/plans and
 * /:username/shop/points.
 */
export const getShop = async (
  username: string,
  kind: 'plans' | 'points'
): Promise<ShopResponse> => {
  const response = await axios.get(
    `${API_URL}/customer/shop/${encodeURIComponent(username)}/${kind}`
  );
  return {
    organizationId: response.data.organizationId ?? null,
    host: response.data.host,
    products: response.data.products ?? [],
  };
};

/**
 * Start a card payment for a point pack.
 *
 * `email` is only used when signed OUT — buying before you have an account is
 * the normal path. Signed in, the server takes the address from the verified
 * token and ignores anything sent here.
 */
export const purchasePointPack = async (
  productId: number,
  email?: string
): Promise<PointPurchaseInit> => {
  const response = await axios.post(
    `${API_URL}/customer/purchase/point-pack`,
    { productId, ...(email ? { email } : {}), language: i18n.language?.startsWith('ja') ? 'ja' : 'en' },
    authConfig()
  );
  return response.data;
};

/**
 * Grant the points once Stripe reports success. Idempotent server-side — the
 * webhook calls the same code, so a double call is a no-op rather than a
 * double grant.
 */
export const confirmPointPurchase = async (
  externalTransactionId: string,
  /** The witness from init, when this checkout minted one. */
  checkoutClaimToken?: string
): Promise<{
  status: string;
  points?: number;
  balance?: number;
  grantToken?: string;
  /** True when this purchase created the account and signed them in. */
  signedIn?: boolean;
}> => {
  const response = await axios.post(
    `${API_URL}/customer/purchase/confirm`,
    { externalTransactionId, ...(checkoutClaimToken ? { checkoutClaimToken } : {}) },
    authConfig()
  );
  const signedIn = adoptCheckoutSession(response.data?.sessionToken);
  // Stashed the moment it arrives, so a buyer who came from a booking page can
  // go back and finish it. Never for someone signed in: they book as
  // themselves, and a receipt left lying in the tab would only be a stale
  // pointer to a wallet nobody is using.
  if (response.data?.grantToken && !getCustomerToken()) {
    setPurchaseGrant(response.data.grantToken);
  }
  return { ...response.data, signedIn };
};

export interface SubscriptionPurchaseInit {
  /** Null when the first invoice needed no authentication — already active. */
  clientSecret: string | null;
  subscriptionId: string;
  status: string;
  amount: number;
  currency: string;
  publishableKey: string;
  connectedAccountId: string;
  /** Present only when the plan went live here and now, with no 3-D Secure. */
  grantToken?: string;
  /** The witness for a signed-out buyer with no account — see PointPurchaseInit. */
  checkoutClaimToken?: string;
  /** True when this purchase created the account and signed them in. */
  signedIn?: boolean;
}

/** Start a subscription. Same identity rules as a point pack. */
export const purchaseSubscription = async (
  productId: number,
  email?: string
): Promise<SubscriptionPurchaseInit> => {
  const response = await axios.post(
    `${API_URL}/customer/purchase/subscription`,
    { productId, ...(email ? { email } : {}), language: i18n.language?.startsWith('ja') ? 'ja' : 'en' },
    authConfig()
  );
  // A plan whose first invoice needed no authentication is already live, so the
  // session arrives here rather than at sync.
  const signedIn = adoptCheckoutSession(response.data?.sessionToken);
  if (response.data?.grantToken && !getCustomerToken()) {
    setPurchaseGrant(response.data.grantToken);
  }
  return { ...response.data, signedIn };
};

/**
 * Re-read the subscription from Stripe after card authentication, so the
 * portal is correct immediately rather than after the webhook lands.
 */
export const syncSubscription = async (
  stripeSubscriptionId: string,
  checkoutClaimToken?: string
): Promise<{ status: string; signedIn: boolean }> => {
  const response = await axios.post(
    `${API_URL}/customer/subscription/sync`,
    {
      stripeSubscriptionId,
      language: i18n.language?.startsWith('ja') ? 'ja' : 'en',
      ...(checkoutClaimToken ? { checkoutClaimToken } : {}),
    },
    authConfig()
  );
  // The usual path for a card that needed authentication: this is the call that
  // learns the plan is live, so this is where both the session and the receipt
  // appear.
  const signedIn = adoptCheckoutSession(response.data?.sessionToken);
  if (response.data?.grantToken && !getCustomerToken()) {
    setPurchaseGrant(response.data.grantToken);
  }
  return { status: response.data?.status ?? 'unknown', signedIn };
};

/** A card, described well enough to recognise. Never more than that. */
export interface SubscriptionCard {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

/** One thing that happened to a wallet — points moving, or money charged. */
export type CustomerTransaction =
  | {
      kind: 'points';
      id: string;
      at: string;
      delta: number;
      reason: string;
      bookingId: number | null;
    }
  | {
      kind: 'payment';
      id: string;
      at: string;
      amount: number;
      currency: string;
      purpose: string;
      productName: string | null;
    };

/**
 * Everything that happened to this wallet, newest first.
 *
 * Points and card payments are two halves of one history: someone paying
 * monthly for a plan saw no record of ever having paid when only the point
 * ledger was shown.
 */
export const getCustomerTransactions = async (
  organizationId: number
): Promise<{ balance: number; entries: CustomerTransaction[] }> => {
  const response = await axios.get(`${API_URL}/customer/transactions`, {
    params: { organizationId },
    ...authConfig(),
  });
  return {
    balance: response.data.balance ?? 0,
    entries: response.data.entries ?? [],
  };
};

/** Undo a pending cancellation, so the plan keeps renewing. */
export const resumeSubscription = async (subscriptionId: string): Promise<void> => {
  await axios.post(`${API_URL}/customer/subscription/resume`, { subscriptionId }, authConfig());
};

/**
 * Move a live plan onto another one. NEVER `purchaseSubscription` for this —
 * that starts a SECOND plan and the customer ends up paying for both. Stripe
 * prorates, so what is charged is the difference for the time left, billed
 * right after the change lands.
 */
export const changeSubscriptionPlan = async (
  subscriptionId: string,
  productId: number
): Promise<void> => {
  await axios.post(
    `${API_URL}/customer/subscription/change-plan`,
    { subscriptionId, productId },
    authConfig()
  );
};

/** The card this plan charges next. Null when Stripe has none on file. */
export const getSubscriptionCard = async (
  subscriptionId: string
): Promise<SubscriptionCard | null> => {
  const response = await axios.get(`${API_URL}/customer/subscription/payment-method`, {
    params: { subscriptionId },
    ...authConfig(),
  });
  return response.data?.card ?? null;
};

/** Begin replacing the card — a SetupIntent, because changing a card is not a charge. */
export const startCardUpdate = async (
  subscriptionId: string
): Promise<{ clientSecret: string | null; publishableKey: string; connectedAccountId: string }> => {
  const response = await axios.post(
    `${API_URL}/customer/subscription/payment-method/start`,
    { subscriptionId },
    authConfig()
  );
  return response.data;
};

/** Point the plan at the card just confirmed. */
export const finishCardUpdate = async (
  subscriptionId: string,
  paymentMethodId: string
): Promise<void> => {
  await axios.post(
    `${API_URL}/customer/subscription/payment-method`,
    { subscriptionId, paymentMethodId },
    authConfig()
  );
};

/** Cancel at period end — they keep what they already paid for. */
export const cancelSubscription = async (subscriptionId: string): Promise<void> => {
  await axios.post(
    `${API_URL}/customer/subscription/cancel`,
    { subscriptionId },
    authConfig()
  );
};

// ========== PORTAL ==========

export const getMe = async (): Promise<CustomerMe> => {
  const response = await axios.get(`${API_URL}/customer/me`, authConfig());
  return { email: response.data.email, wallets: response.data.wallets ?? [] };
};

export const getPointsHistory = async (
  organizationId: number,
  opts: { limit?: number; offset?: number } = {}
): Promise<{ balance: number; entries: PointsHistoryEntry[]; total: number }> => {
  const response = await axios.get(`${API_URL}/customer/points/history`, {
    params: { organizationId, ...opts },
    ...authConfig(),
  });
  return {
    balance: response.data.balance ?? 0,
    entries: response.data.entries ?? [],
    total: response.data.total ?? 0,
  };
};

/**
 * What this person may do on a meeting type. Works signed OUT too — that is how
 * the booking page learns it needs to show the sign-in prompt at all.
 */
export const getEntitlement = async (
  meetingTypeId: number,
  productId?: number | null,
  /**
   * The duration being booked. A page priced in POINTS prices per duration just
   * as a money one does, so leaving this out resolved the cost to zero and told
   * a customer with a full wallet that they were short.
   */
  durationMinutes?: number | null
): Promise<EntitlementResolution> => {
  /**
   * Signed out, the receipt is the only thing that says who this is — someone
   * who bought points a minute ago and came back to finish booking. Without it
   * the page tells them to sign in for points they already own.
   *
   * A header, not a query parameter: this is a GET, and putting a token in the
   * URL puts it in access logs and referrers.
   */
  const grant = getCustomerToken() ? null : getPurchaseGrant();

  const response = await axios.get(`${API_URL}/customer/entitlement`, {
    params: {
      meetingTypeId,
      ...(productId ? { productId } : {}),
      ...(durationMinutes ? { durationMinutes } : {}),
    },
    ...authConfig(),
    ...(grant ? { headers: { 'X-Purchase-Grant': grant } } : {}),
  });
  return response.data;
};

/** Point packs and plans that unlock a meeting type. Public. */
export const getPurchasable = async (
  meetingTypeId: number
): Promise<PurchasableResponse> => {
  const response = await axios.get(`${API_URL}/customer/purchasable`, {
    params: { meetingTypeId },
  });
  return {
    accessRequirement: response.data.accessRequirement ?? 'none',
    pointCost: response.data.pointCost ?? null,
    products: response.data.products ?? [],
  };
};

const customerService = {
  getShop,
  getShopSummary,
  purchasePointPack,
  confirmPointPurchase,
  purchaseSubscription,
  syncSubscription,
  cancelSubscription,
  login,
  requestPasswordReset,
  completePasswordReset,
  setPassword,
  logout,
  getMe,
  getPointsHistory,
  getCustomerTransactions,
  resumeSubscription,
  changeSubscriptionPlan,
  getSubscriptionCard,
  startCardUpdate,
  finishCardUpdate,
  getEntitlement,
  getPurchasable,
  getCustomerToken,
  setCustomerToken,
  clearCustomerToken,
  getPurchaseGrant,
  setPurchaseGrant,
  clearPurchaseGrant,
};

export default customerService;
